import { EpiImage } from '@api/types';
import { useTheme } from '@mui/material';
import DOMPurify from 'dompurify';

export type ImageResizerParams = {
  width?: number;
  height?: number;
  format?: 'webp' | 'jpg' | 'png';
  quality?: number;
};

// TODO [kf] - revisit this logic
export const getEpiImageResizerUrl = (image: EpiImage, params?: ImageResizerParams): string => {
  if (typeof image === 'string') {
    return image.indexOf('?') > 0 ? image : getImageResizerUrl(image, params) || '';
  }
  const url = image?.url;
  const resizerRequestParams = new URLSearchParams(params as Record<string, string>);
  return `${url}?${resizerRequestParams.toString()}` || '';
};

export const getImageResizerUrl = (imageUrl: string, params?: ImageResizerParams): string => {
  if (!imageUrl) {
    return '';
  }
  const resizerRequestParams = new URLSearchParams(params as Record<string, string>);
  return `${imageUrl}?${resizerRequestParams.toString()}` || '';
};

export const isEpiImage = (image?: EpiImage) => {
  return image && typeof image === 'object' && image?.url;
};

export function normalizeImageUrl(imageUrl?: string): string {
  if (!imageUrl) return '';

  const isDev = process.env.NODE_ENV === 'development';
  const imageDomain = process.env.REACT_APP_IMAGE_DOMAIN;

  if (isDev && imageDomain && imageUrl.includes('localhost:5000')) {
    return imageUrl.replace('localhost:5000', imageDomain);
  }

  const isRelative = imageUrl.startsWith('/') && !imageUrl.startsWith('//');
  if (isDev && imageDomain && isRelative) {
    return `https://${imageDomain}${imageUrl}`;
  }
  return imageUrl;
}

export function transformImagesInHtml(html: string): string {
  const SCALE_FACTOR = 1.5;
  const MIN_WIDTH = 300;
  const MAX_WIDTH = 1200;
  const DEFAULT_WIDTH = 800;

  function computeVariants(intrinsic: number) {
    const base = Math.min(MAX_WIDTH, Math.max(Math.floor(intrinsic * SCALE_FACTOR), MIN_WIDTH));
    return [
      {
        media: '(max-width:600px)',
        width: Math.max(Math.floor(base * 0.5), MIN_WIDTH),
        quality: 60,
      },
      { media: '(max-width:1200px)', width: Math.max(base, MIN_WIDTH), quality: 70 },
      { width: MAX_WIDTH, quality: 85 },
    ];
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  doc.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src') || '';
    const rawWidth = img.getAttribute('width');
    const intrinsic = rawWidth ? parseInt(rawWidth, 10) : DEFAULT_WIDTH;
    const variants = computeVariants(intrinsic);

    const picture = document.createElement('picture');
    variants.forEach(({ media, width, quality }) => {
      const source = document.createElement('source');
      if (media) source.media = media;
      const url = getImageResizerUrl(src, { width, format: 'webp', quality });
      source.srcset = normalizeImageUrl(url);
      picture.appendChild(source);
    });

    const fallback = img.cloneNode(true) as HTMLImageElement;
    const srcset = variants
      .map(
        ({ width, quality }) =>
          `${normalizeImageUrl(
            getImageResizerUrl(src, { width, format: 'webp', quality })
          )} ${width}w`
      )
      .join(', ');

    fallback.src = normalizeImageUrl(
      getImageResizerUrl(src, {
        width: variants[1].width,
        format: 'webp',
        quality: variants[1].quality,
      })
    );
    fallback.srcset = srcset;
    fallback.sizes = '(max-width:600px) 100vw, (max-width:1200px) 80vw, 1200px';
    fallback.loading = 'lazy';
    fallback.decoding = 'async';
    fallback.style.maxWidth = '100%';
    fallback.style.height = 'auto';
    if (!fallback.alt) fallback.alt = img.alt || '';

    picture.appendChild(fallback);
    img.replaceWith(picture);
  });

  return DOMPurify.sanitize(doc.body.innerHTML);
}

export interface ResponsiveBackgroundParams {
  imageUrl?: string;
  resizerParams: ImageResizerParams;
}

export function useResponsiveBackground({
  imageUrl,
  resizerParams,
}: {
  imageUrl?: string;
  resizerParams: ImageResizerParams;
}) {
  const theme = useTheme();
  const MIN_WIDTH = 400;
  const SM_WIDTH = theme.breakpoints.values.md;
  const MD_WIDTH = theme.breakpoints.values.lg;
  const LG_WIDTH = resizerParams.width! || 1400;

  const clamp = (w: number) => Math.min(LG_WIDTH, Math.max(MIN_WIDTH, w));
  const baseUrl = normalizeImageUrl(imageUrl);

  const smUrl = getImageResizerUrl(baseUrl, {
    ...resizerParams,
    width: clamp(SM_WIDTH),
    quality: 70,
  });
  const mdUrl = getImageResizerUrl(baseUrl, {
    ...resizerParams,
    width: clamp(MD_WIDTH),
    quality: 80,
  });
  const lgUrl = getImageResizerUrl(baseUrl, {
    ...resizerParams,
  });

  return {
    backgroundImage: {
      xs: `url(${normalizeImageUrl(smUrl)})`,
      md: `url(${normalizeImageUrl(mdUrl)})`,
      lg: `url(${normalizeImageUrl(lgUrl)})`,
    },
  };
}
